import React from 'react';
import { Type, Check, AlertTriangle, BarChart, Code } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';

/**
 * Компонент отображения результатов расчета размера текста
 */
const ResultsOverview = ({ results }) => {
  if (!results) return null;

  // Форматирование размеров для отображения
  const formatSize = (size, unit) => {
    if (unit === '%') return `${size}%`;
    if (unit === 'vw') return `${size}vw`;
    return `${size}${unit}`;
  };

  // Определение цвета для оценки читаемости
  const getReadabilityColor = (score) => {
    if (score >= 90) return 'green';
    if (score >= 70) return 'emerald';
    if (score >= 50) return 'yellow';
    return 'red';
  };

  // Расшифровка целевой аудитории
  const getAudienceDescription = (audience) => {
    switch (audience) {
      case 'general': return 'Общая аудитория';
      case 'elderly': return 'Пожилые люди';
      case 'children': return 'Дети и подростки';
      case 'visually-impaired': return 'Люди с нарушениями зрения';
      case 'dyslexic': return 'Люди с дислексией';
      default: return 'Не указана';
    }
  };

  return (
    <ToolResult title="Результаты расчета размера текста">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Размер текста */}
        <div className="rounded-lg bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 p-5 border border-indigo-100 dark:border-indigo-800/50 shadow-sm">
          <h3 className="text-base font-medium text-indigo-700 dark:text-indigo-300 mb-1.5 flex items-center gap-1.5">
            <Type className="w-4 h-4"/>
            Оптимальный размер текста
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-indigo-800 dark:text-indigo-200" aria-live="polite">
              {results.fontSizes.px}px
            </span>
            <span className="text-sm font-medium text-indigo-600 dark:text-indigo-400">
              ({results.fontSizes.rem}rem)
            </span>
          </div>
          <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">
            Для {getAudienceDescription(results.targetAudience)}, {results.mediaType === 'screen' ? 'экранный режим' : 'печатный режим'}
          </p>
        </div>

        {/* Читаемость */}
        <div className="rounded-lg bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-900/30 dark:to-green-900/30 p-5 border border-emerald-100 dark:border-emerald-800/50 shadow-sm">
          <h3 className="text-base font-medium text-emerald-700 dark:text-emerald-300 mb-1.5 flex items-center gap-1.5">
            <BarChart className="w-4 h-4"/>
            Оценка читаемости
          </h3>
          <div className="flex items-center">
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-5 mr-3">
              <div 
                className={`h-5 rounded-full bg-${getReadabilityColor(results.readability.score)}-500 dark:bg-${getReadabilityColor(results.readability.score)}-600`}
                style={{ width: `${results.readability.score}%` }}
              ></div>
            </div>
            <span className="text-lg font-bold text-emerald-800 dark:text-emerald-200 min-w-[50px] text-right">
              {results.readability.score}/100
            </span>
          </div>
          <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
            {results.readability.score >= 90 && 'Отличная читаемость для выбранных параметров'}
            {results.readability.score >= 70 && results.readability.score < 90 && 'Хорошая читаемость, но есть возможности для улучшения'}
            {results.readability.score >= 50 && results.readability.score < 70 && 'Средняя читаемость, рекомендуются улучшения'}
            {results.readability.score < 50 && 'Низкая читаемость, требуется корректировка параметров'}
          </p>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Размер в различных единицах" 
          value={`${results.fontSizes.px}px | ${results.fontSizes.pt}pt | ${results.fontSizes.em}em | ${results.fontSizes.vw}vw`} 
          color="indigo" 
          icon={<Type className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Соответствие стандартам" 
          value={results.standards.standardsCompliance ? 'Соответствует требованиям' : 'Требуется корректировка'} 
          color={results.standards.standardsCompliance ? 'green' : 'orange'} 
          icon={results.standards.standardsCompliance ? <Check className="w-4 h-4"/> : <AlertTriangle className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Минимальный рекомендуемый размер" 
          value={`${results.readability.minSizeForDistance}px (с учетом расстояния ${results.readability.viewingDistance} см)`} 
        />

        {/* CSS-код для применения */}
        <div className="mt-6">
          <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2 flex items-center">
            <Code className="w-4 h-4 mr-2 text-indigo-500" />

          </h4>
          <pre className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg overflow-x-auto text-xs font-mono text-gray-800 dark:text-gray-300 border border-gray-200 dark:border-gray-700">
            {results.cssCode}
          </pre>
        </div>

        {/* Адаптивный CSS-код */}
        <div className="mt-4">
          <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2 flex items-center">
            <Code className="w-4 h-4 mr-2 text-indigo-500" />
            Адаптивный CSS для разных устройств
          </h4>
          <pre className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg overflow-x-auto text-xs font-mono text-gray-800 dark:text-gray-300 border border-gray-200 dark:border-gray-700">
            {results.responsiveCss}
          </pre>
        </div>
        
        {/* Рекомендации по улучшению */}
        {results.standards.improvements.length > 0 && (
          <div className="mt-6 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-100 dark:border-amber-800">
            <h4 className="text-sm font-medium text-amber-800 dark:text-amber-300 mb-2 flex items-center">
              <AlertTriangle className="w-4 h-4 mr-2 text-amber-500" />
              Рекомендации по улучшению
            </h4>
            <ul className="space-y-1 text-sm text-amber-700 dark:text-amber-400">
              {results.standards.improvements.map((improvement, index) => (
                <li key={index} className="flex items-start">
                  <span className="mr-2">•</span>
                  {improvement}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </ToolResult>
  );
};

export default ResultsOverview;